import type { FC } from 'react'

import { Card, Select } from '@skytech/ui'
import type { FieldProps } from 'formik'
import { Field } from 'formik'

import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'

const MONTH_INTERVALS = [0, 3, 6, 12, 18, 23, 24, 30, 36, 48, 60]

const Intervals: FC<{ hideOrder?: boolean; hideCover?: boolean }> = ({ hideOrder, hideCover }) => {
  const translate = useTranslation()

  const getMonthOptions = ({
    recommendation,
    monthIntervals = MONTH_INTERVALS,
    altTexts = {},
  }: {
    monthIntervals?: number[]
    altTexts?: Record<number, string>
    recommendation: number
  }) =>
    monthIntervals.map(interval => ({
      value: interval,
      label:
        altTexts[interval] ||
        `${interval} ${translate('months')} ${
          recommendation === interval ? `[ ${translate('recommended')} ]` : ''
        }`,
    }))

  const paymentIntervalOptions = getMonthOptions({
    recommendation: 3,
    altTexts: { 0: translate('Partial down payments unavailable') },
    monthIntervals: [0, 2, 3, 6, 12, 18, 23, 24, 30, 36, 48],
  })
  const orderIntervalOptions = getMonthOptions({
    recommendation: 24,
    altTexts: {
      0: translate('Employees may order again immediately'),
      1: translate('1 month'),
    },
    monthIntervals: [0, 1, 3, 6, 12, 18, 23, 24, 30, 36, 42, 48],
  })
  const coverIntervalOptions = getMonthOptions({
    recommendation: 24,
    altTexts: {
      0: translate('Full coverage immediately'),
    },
  })

  return (
    <Card className="time-intervals">
      <h3>
        <Translation id="Time intervals" />
      </h3>

      <Field name="paymentInterval">
        {({ field, form }: FieldProps) => (
          <Select
            id="paymentInterval"
            label={<Translation id="Down payments over salary" />}
            options={paymentIntervalOptions}
            marginBottom={16}
            {...field}
            onChange={option => form.setFieldValue(field.name, option?.value)}
          />
        )}
      </Field>

      {!hideCover && (
        <Field name="coverInterval">
          {({ field, form }: FieldProps) => (
            <Select
              id="coverInterval"
              label={<Translation id="Time until max amount coverage" />}
              options={coverIntervalOptions}
              marginBottom={16}
              {...field}
              onChange={option => form.setFieldValue(field.name, option?.value)}
            />
          )}
        </Field>
      )}

      {!hideOrder && (
        <Field name="orderInterval">
          {({ field, form }: FieldProps) => (
            <Select
              id="orderInterval"
              label={<Translation id="Minimum time between orders" />}
              options={orderIntervalOptions}
              {...field}
              onChange={option => form.setFieldValue(field.name, option?.value)}
            />
          )}
        </Field>
      )}
    </Card>
  )
}

export default Intervals
